import type { ResearchRecord } from "@pokifolio/contracts";
import { Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";

import { useApi } from "../../app/api-context";
import { Card } from "../../components/ui/card";
import { useResearchRecord } from "../research/research-queries";
import { type CardFormValues, EMPTY_CARD } from "./card-values";
import { PortfolioForm } from "./portfolio-form";
import { usePortfolioChange } from "./portfolio-queries";

/** Add a card. Opened from an approved research record, the form starts with what is already known. */
export function PortfolioNewPage({ researchId }: { researchId: string | undefined }) {
  const api = useApi();
  const navigate = useNavigate();
  const add = usePortfolioChange(api.addPortfolioItem);
  const research = useResearchRecord(researchId ?? "", researchId !== undefined);

  if (researchId !== undefined && research.isPending) {
    return (
      <Card>
        <p className="text-sm text-muted-foreground">Loading the research…</p>
      </Card>
    );
  }
  const initial = research.data === undefined ? EMPTY_CARD : fromResearch(research.data.research);

  return (
    <div className="flex flex-col gap-6">
      <Link to="/portfolio" className="inline-flex items-center gap-1 text-sm text-primary hover:underline">
        <ArrowLeft className="size-4" aria-hidden="true" />
        Portfolio
      </Link>
      <div>
        <h1 className="text-2xl font-semibold text-foreground">Add a card</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {research.data === undefined
            ? "Only the name is needed. You can add photos on the next page."
            : "Filled in from the research. Check the details, then add photos on the next page."}
        </p>
      </div>
      <Card>
        <PortfolioForm
          initial={initial}
          submitLabel="Add card"
          pending={add.isPending}
          error={add.error}
          onSubmit={(input) =>
            add.mutate(input, {
              onSuccess: (item) => void navigate({ to: "/portfolio/$itemId", params: { itemId: item.id } }),
            })
          }
        />
      </Card>
    </div>
  );
}

function lowerOf(a: string | undefined, b: string | undefined): string | undefined {
  if (a === undefined || b === undefined) return a ?? b;
  return Number(a) <= Number(b) ? a : b;
}

function fromResearch(research: ResearchRecord): CardFormValues {
  const { card, assessment, price, listing } = research;
  const graded = assessment?.cardType === "graded";
  return {
    ...EMPTY_CARD,
    name: card.name,
    setName: card.setName,
    cardNumber: card.number,
    language: "English",
    cardType: graded ? "graded" : "raw",
    condition: !graded ? (assessment?.overall ?? "") : "",
    gradingCompany: graded ? (assessment.gradingCompany ?? "") : "",
    grade: graded && assessment.grade !== null ? String(assessment.grade) : "",
    certificateNumber: graded ? (assessment.certificateNumber ?? "") : "",
    // What was paid is not known yet: start from the lower of the checked total and the approved maximum.
    purchasePrice: lowerOf(price?.totalUsd, research.decision?.approvedMaxUsd ?? undefined) ?? "",
    purchaseCurrency: "USD",
    purchaseDate: new Date().toISOString().slice(0, 10),
    boughtFrom: listing.website,
    researchId: research.id,
  };
}
